/**
 * Items on the `quoflow.quoflow.quoflow.api.items.*` methods — the new namespace this migration
 * moves onto (see `bosun.ts`'s header). Only the CATALOGUE SEARCH list (`get_items`) and the
 * small per-item lookups (`get_stock`, `get_supplier_emails`, `get_suppliers_for_item`,
 * `get_blanket_orders`, `get_purchase_history`) move here.
 *
 * `useItemsByCode` and `useItem` — the batch/single item-by-code reads used by `/rfq`, `/compare`,
 * `/quote`, `/revisions` and `SupplierQuotationsPage` — are NOT part of this task (no live-verified
 * endpoint for them yet) and stay on the old `getResource`/`getResourceDoc` + `mappers/item.ts`
 * path below, untouched.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import { codeKey, codesFromKey } from './codeKey'
import type { PriceRow } from '../inHousePrice'
import { currentClerkToken } from './frappeSession'
import { money, num, str, type WireList } from './wire'
import { lookupSupplierAddresses } from './supplierContacts'
import { getResource, getResourceDoc } from './frappe'
import {
  toBlanketOrderDetails,
  toBlanketOrderSummaries,
  type WireActiveBlanketOrders,
} from './activeBlanketOrders'
import { ITEM_DOCTYPE, toItem as toItemLegacy } from './mappers/item'
import { num as numLegacy, str as strLegacy } from './mappers/coerce'
import { money as moneyLegacy } from '../money'
import { qk, type ItemQuery } from './keys'
import { getItemScope, itemScopeParams, planItemSearch } from './itemScope'
import { queryClient } from '../queryClient'
import { getBaseCurrency } from './baseCurrency'
import { parseMatchResponse, type ItemMatch } from '../searchMatch'
import type { BlanketOrderDetail } from '../blanketOrders'

export type { ItemMatch, MatchCandidate } from '../searchMatch'
import { ITEM_CATEGORIES, type ItemCategory } from '../types'
import type { Money, Item, ItemContract } from '../types'

const CATEGORY_SET = new Set<string>(ITEM_CATEGORIES)

/**
 * Stable empties, so a still-loading table is handed the SAME Map every render rather than a
 * fresh one — a new Map each render defeats every downstream memo.
 *
 * Typed as `Map`, not `ReadonlyMap`, only because that is the shape five consuming screens
 * already expect. Never written to.
 */
const EMPTY_CODES: ReadonlySet<string> = new Set()
const EMPTY_ITEMS: Map<string, Item> = new Map()
const EMPTY_PRICE_SOURCES: Map<string, PriceSource> = new Map()

/* ============ items.get_items ============ */

export interface WireItem {
  item_code: string
  item_name: string
  category?: string | null
  item_group?: string | null
  description?: string | null
  stock_uom: string
  is_stock_item?: number
  impa_number?: string | null
  uom_conversions?: { uom: string; conversion_factor: number }[]
}

export function toItem(w: WireItem): Item {
  return {
    code: w.item_code,
    name: w.item_name,
    // Defect B2: the server currently sends the IMPA item_group in `category`. Only accept a
    // value that is genuinely one of our four; otherwise leave it unset. See task-5-brief.md.
    category: w.category && CATEGORY_SET.has(w.category) ? (w.category as ItemCategory) : undefined,
    group: str(w.item_group),
    base_uom: w.stock_uom,
    impa_number: w.impa_number ?? undefined,
    uom_conversions: (w.uom_conversions ?? []).map((c) => ({ uom: c.uom, factor: num(c.conversion_factor, 1) })),
    // `items.get_items` carries none of these four any more — each now lives behind its own
    // lookup, fetched separately and NOT per catalogue row (a list can run to 18,836 items; a
    // per-row fetch here would be an N+1 across the whole catalogue). Left unset rather than
    // defaulted, same reasoning as `on_hand`: a blank/0/[] here would read as "no stock", "no
    // suppliers" or "no contract", which is a fact this endpoint does not know.
    on_hand: undefined, // → `items.get_stock`
    base_price: undefined, // → Item Price / a pricing endpoint (unrelated to this task)
    candidate_suppliers: [], // → `items.get_suppliers_for_item`
    contracts: [], // → `items.get_blanket_orders`
  }
}

export function useItems(query: ItemQuery = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.items(query),
    // `enabled: false` — a picker that opens on the matcher's shortlist does not also fetch an
    // unsearched first page of the catalogue (Akhil, 2026-09-16); it searches when something is typed.
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      /*
        ---- The enquiry TYPE scopes the catalogue, but barely ----

        The server does filter on it, and `CatalogPickerModal` has always passed it — it was
        simply never put on the wire, so a provision enquiry's picker offered all 18,836 items.
        Sending it looked like a clean fix. Measured against the live bench, it is not:

          enquiry_type=Provision   →     9 items, 1 group  ("Non stock Provision")
          enquiry_type=Technical   →    93 items, 4 groups
          (unfiltered)             → 18,836 items

        Nine items. The mapping behind it (`Enquiry Type.enquiry_item_group`) was configured for
        almost nothing, the same coverage problem `Item Group Supplier` has — and by 2026-09-22 it
        is gone: the doctype's fields are `sync_status`, `type_name`, `description`, `platform_id`,
        and EVERY non-empty `enquiry_type` answers zero rows. `D45` asks for it back.

        So the type is sent, and the search FALLS BACK to the whole catalogue when it finds
        nothing. Scoping where the mapping exists; a working search everywhere else. `filtered`
        tells the screen which happened, so a purchaser is never quietly shown a different
        catalogue from the one they asked for.

        The fallback used to run only when the CALLER named a type — a type from the page's scope
        got none, so the picker on any enquiry with a type came back empty and stayed empty
        ("is the search api not working?", 2026-09-22). `planItemSearch` plans both the same way.
      */
      const plan = planItemSearch(query, getItemScope())
      const page = await callGet<WireList<WireItem>>('items', 'get_items', plan.params)
      if (page.data.length > 0 || plan.fallback === null) {
        return { data: page.data.map(toItem), total: page.total ?? page.data.length, filtered: plan.fallback !== null }
      }

      // The type found nothing. A picker with no results is worse than one showing more than it
      // strictly should — the purchaser can read an item name and decide for themselves.
      const all = await callGet<WireList<WireItem>>('items', 'get_items', plan.fallback)
      return { data: all.data.map(toItem), total: all.total ?? all.data.length, filtered: false }
    },
    // `CatalogPickerModal` re-runs this on every keystroke over an 18,836-item catalogue —
    // without this, the list blanks between keystrokes instead of updating in place once the
    // new page lands. Restored (was dropped somewhere upstream of Task 8).
    placeholderData: (previous) => previous,
  })
}

/* ============ items.get_stock ============ */

export type WireStock =
  | {
      stock_enabled: true
      item_code: string
      qty: number
      /** Already committed to other orders. Live since the ERP was connected (2026-08-29). */
      reserved_qty?: number
      warehouse_breakdown?: Array<{ warehouse: string; actual_qty: number; reserved_qty: number }>
    }
  | { stock_enabled: false; message: string }

export interface ItemStock {
  stock_enabled: boolean
  /** On the shelf, including what is already promised elsewhere. */
  qty: number | null
  /** Committed to other orders. */
  reserved: number | null
  /** What can actually be sold: `qty - reserved`. THE number a purchaser decides on. */
  available: number | null
}

/**
 * On-hand, reserved, and what is actually sellable.
 *
 * The distinction is not cosmetic. Measured live: `{"qty": 28.0, "reserved_qty": 33.0}` — 28 on
 * the shelf with 33 already committed is not 28 to sell, it is 5 SHORT. Reporting `qty` as the
 * sellable figure tells a purchaser they can supply from stock when they cannot, on the number
 * that decides whether a line needs to go to suppliers at all.
 *
 * `available` is NOT clamped at zero. A negative is a real state — stock already oversold — and
 * flooring it would hide precisely the case worth seeing.
 */
/*
  Two decimal places, applied to what the SERVER sends and to our own subtraction.

  `reserved_qty` is summed across warehouses server-side in floating point, and the sum arrives
  with its full error term — measured live on item 0001020024:

    warehouse_breakdown: [{reserved_qty: 0.7}, {reserved_qty: 158.1}]
    reserved_qty:        158.79999999999998

  Rendered raw that reached the screen as "158.79999999999998 reserved", which reads as a broken
  number rather than a quantity. Rounding HERE fixes every screen at once, rather than each
  display formatting around it — and `available` is rounded too, since subtracting two floats
  reintroduces the same tail.

  Two places, not zero: a genuine fractional quantity (2.5 KG, 0.25 DRUM) is a real stock figure
  and rounding it to whole units would misstate what is on the shelf.
*/
const round2 = (n: number): number => Math.round(n * 100) / 100

export function toStock(w: WireStock): ItemStock {
  if (!w.stock_enabled) return { stock_enabled: false, qty: null, reserved: null, available: null }
  const qty = round2(num(w.qty))
  // Absent means nothing is committed, which is a real answer — unlike `qty`, whose absence
  // would be a gap. Items with no reservations simply omit it.
  const reserved = round2(num(w.reserved_qty))
  return { stock_enabled: true, qty, reserved, available: round2(qty - reserved) }
}

export function useStock(itemCode: string) {
  return useQuery({
    queryKey: ['items', itemCode, 'stock'] as const,
    queryFn: async () => toStock(await callGet<WireStock>('items', 'get_stock', { item_code: itemCode })),
    enabled: !!itemCode,
    // Opted OUT of the global `refetchOnWindowFocus: true` (main.tsx) — this is one query PER
    // LINE ITEM, deliberately fired only once `LineRow.tsx`'s `focused` latch trips (its own
    // comment: an enquiry can carry many lines, and firing this for every row on render would be
    // an N+1 against the live server). The global default respects `staleTime`, so a purchaser
    // who has triaged 30 lines and switches tabs for a minute would come back to ~30 of these
    // (plus the `useBlanketOrders` below) refetching in one burst — reinstating exactly the N+1
    // the latch exists to avoid. Do not remove this without also revisiting the latch.
    refetchOnWindowFocus: false,
  })
}

/**
 * Stock for a SET of item codes — every matched line on the table, and the match shortlist.
 *
 * ---- Two guards, and neither is optional ----
 *
 * `items.get_stock` takes one item code per call, so a ten-candidate shortlist on a forty-line
 * enquiry is four hundred requests if nothing holds it back. Two things do:
 *
 *  1. **`enabled`** — the caller passes `LineRow`'s `focused` latch, so the shortlist's stock is
 *     fetched only for the row a purchaser actually clicked into, never for the whole table on
 *     render. Same rule, and the same reason, as `useStock` and `useBlanketOrders` above.
 *  2. **`stockEnabled`** — `useStockEnabled`'s single session-cached probe. On this bench
 *     `items.get_stock` answers `{stock_enabled: false, "ERP not configured in Bosun Settings"}`
 *     for every item, and there is no local `Bin` doctype either: the figure lives in an ERP that
 *     Bosun Settings has not been pointed at. Firing ten requests per row to be told "no" ten
 *     times is the exact N+1 the probe exists to prevent, so this fetches NOTHING until the probe
 *     says the deployment can answer.
 *
 * Keyed per code, identically to `useStock`, so the two share one cache entry per item rather
 * than racing each other for the same answer.
 */
export function useStockByCode(codes: string[], enabled: boolean) {
  // `codeKey`, NOT `join(',')` — a real item code contains a comma and splitting it back on one
  // destroyed the code before the request was made. See `codeKey.ts`.
  const key = codeKey(codes)
  const unique = useMemo(() => codesFromKey(key), [key])
  // One probe for the whole deployment, cached for the session — not one per candidate.
  const stockEnabled = useStockEnabled(unique[0])
  const canFetch = enabled && stockEnabled.data === true

  const queries = useQueries({
    queries: unique.map((code) => ({
      // The SAME key `useStock` uses — see the header.
      queryKey: ['items', code, 'stock'] as const,
      queryFn: async () => toStock(await callGet<WireStock>('items', 'get_stock', { item_code: code })),
      enabled: canFetch,
      // Opted out for the same reason `useStock` is: this is one query per candidate, and a
      // window refocus must not refetch the whole shortlist at once.
      refetchOnWindowFocus: false,
    })),
  })

  /**
   * `code → {qty, reserved, available}`. A code is ABSENT rather than zeroed when we could not or
   * did not ask: a `0` here would render as "out of stock", which is a claim we have not earned.
   *
   * All three travel, not just one. The shelf quantity and what is reserved against it are two
   * facts a purchaser reads separately — "28 on the shelf, 33 already promised" says something
   * that a single derived number cannot.
   */
  return useMemo(() => {
    const map = new Map<string, { qty: number; reserved: number; available: number }>()
    for (let i = 0; i < unique.length; i++) {
      const data = queries[i]?.data
      if (data?.stock_enabled && data.qty !== null && data.available !== null) {
        map.set(unique[i], { qty: data.qty, reserved: data.reserved ?? 0, available: data.available })
      }
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unique, queries.map((q) => q.dataUpdatedAt).join(',')])
}

/* ============ blanket orders, as a SELECTION rather than a per-item lookup ============ */

/**
 * Every standing contract a purchaser may put in play.
 *
 * ---- `items.get_active_blanket_orders`, live since 2026-09-03 ----
 *
 * The method is the source. It was empty that morning (all 17 orders `docstatus: 0`, filed as
 * `D19`) and was fixed the same day: it now answers 5 genuinely active, valid-dated contracts
 * carrying 420 rates — WITH their item lists inline, which is why `useBlanketOrderItems` below
 * no longer fetches per contract.
 *
 * The doctype read is kept behind it, running only if the method answers empty again. It is not
 * dead weight: it is the difference between a backend regression showing up as a slightly staler
 * contract list and showing up as an RFQ screen that can price nothing.
 *
 * NOTE the deliberate behaviour change — the method returns ACTIVE contracts only (5), where the
 * fallback returns all 17 including cancelled ones for the picker to grey out. That is what was
 * asked for, and what the endpoint's name means.
 */
export interface BlanketOrderSummary {
  name: string
  /** The Supplier LINK value — grouped and written on. Never shown in preference to the name. */
  supplier: string
  /** Display only; differs from `supplier` on real rows. See `activeBlanketOrders.ts`. */
  supplier_name: string
  /** `Active`, `Cancelled`, `Draft`… — shown on screen; only `Active` may be selected. */
  status: string
  /** `YYYY-MM-DD` or `''`. Only the method carries these; the doctype fallback leaves them blank. */
  valid_from: string
  valid_till: string
}

/** The one status a line may be priced from. Anything else is shown but not selectable. */
export const BLANKET_ORDER_ACTIVE = 'Active'

const BLANKET_ORDER_DOCTYPE = 'Blanket Order'

export function useActiveBlanketOrders() {
  return useQuery({
    queryKey: ['blanket-orders', 'all'] as const,
    queryFn: async (): Promise<BlanketOrderSummary[]> => {
      /*
        The asked-for method, first. A failure is swallowed rather than thrown: it is a source that
        currently returns nothing anyway, and letting it take the contract picker down with it
        would be strictly worse than the fallback it is meant to replace.
      */
      let fromMethod: BlanketOrderSummary[] = []
      try {
        const wire = await callGet<WireActiveBlanketOrders>('items', 'get_active_blanket_orders', {})
        fromMethod = toBlanketOrderSummaries(wire)
        if (fromMethod.length > 0) {
          /*
            The item lists ride along on this same response, so they are seeded into the cache under
            the keys `useBlanketOrderItems` reads. Ticking a contract then costs NOTHING instead of
            a ~1.25s round trip, and ticking four costs nothing instead of four.

            Seeded rather than returned, so the two hooks keep their existing shapes and the
            fallback path (which has no inline items) still works unchanged.
          */
          for (const [name, detail] of toBlanketOrderDetails(wire)) {
            queryClient.setQueryData(['blanket-orders', 'items', name], detail)
          }
        }
      } catch {
        fromMethod = []
      }
      if (fromMethod.length > 0) return fromMethod

      /*
        EVERY order, not only the active ones — the filter moved to the screen.

        Filtering here hid the non-active contracts entirely, which reads as "this supplier has no
        contract" when the truth is "their contract was cancelled". A purchaser choosing between
        two contracts from the same supplier needs to see that one of them is dead, or they will
        go looking for a contract that is right there and simply not usable.

        `BlanketOrderBar` shows the status and only lets an Active one be selected, so nothing can
        be priced from a cancelled contract — the information is surfaced without being actionable.
      */
      const page = await getResource<Record<string, unknown>>(BLANKET_ORDER_DOCTYPE, {
        fields: ['name', 'supplier', 'status'],
        limit: 200,
        orderBy: 'supplier asc',
      })
      return page.data
        .map((row) => ({
          name: str(row.name),
          supplier: str(row.supplier),
          // The doctype read asks for no separate label, so the link value is all there is.
          supplier_name: str(row.supplier),
          status: str(row.status),
          // Not requested on this path; blank rather than guessed.
          valid_from: '',
          valid_till: '',
        }))
        .filter((o) => o.name !== '' && o.supplier !== '')
    },
    // Contracts are renegotiated in months, not while somebody triages an enquiry.
    staleTime: 30 * 60_000,
  })
}

/**
 * The item lists of the SELECTED orders — one request per order, cached per order.
 *
 * This is the whole point of making blanket orders a selection: the old shape asked
 * `items.get_blanket_orders` PER ITEM, which is a request per row of the table for an answer that
 * is almost always empty. Here the purchaser names a handful of contracts and each one's full
 * item list arrives once (~1.25s, 36–180 items apiece), after which every line on the table is a
 * Map lookup costing nothing.
 *
 * Per-order query keys, so adding a fourth contract to a selection of three fetches only the
 * fourth — and de-selecting and re-selecting is free.
 */
export function useBlanketOrderItems(orderNames: string[]) {
  const key = [...new Set(orderNames.filter(Boolean))].sort().join('\u0000')
  const unique = useMemo(() => (key ? key.split('\u0000') : []), [key])

  const queries = useQueries({
    queries: unique.map((name) => ({
      queryKey: ['blanket-orders', 'items', name] as const,
      queryFn: async () => {
        const w = await callGet<WireBlanketOrderDetail>('items', 'get_blanket_order_item', {
          blanket_order: name,
        })
        return toBlanketOrderDetail(w)
      },
      staleTime: 30 * 60_000,
    })),
  })

  const data = useMemo(
    () => queries.map((q) => q.data).filter((d): d is BlanketOrderDetail => d !== undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queries.map((q) => q.dataUpdatedAt).join(',')],
  )
  /*
    An EMPTY selection is a SETTLED state, not a loading one — and `isPending` alone would not say
    so. A disabled or never-started query sits at `status: 'pending'` in React Query v5, so this
    guards on the selection being non-empty AND uses `isLoading` (`isPending && isFetching`), which
    is false for anything not actually in flight. Reading `isPending` here would hold the RFQ
    page's "preparing" panel open forever whenever no contract is selected.
  */
  const isPending = unique.length > 0 && queries.some((q) => q.isLoading)
  return { data, isPending }
}

interface WireBlanketOrderDetail {
  name?: unknown
  supplier?: unknown
  currency?: unknown
  status?: unknown
  is_expired?: unknown
  items?: unknown
}

/**
 * The live shape (measured 2026-08-29):
 *
 *   {"name":"NATIONAL DAIRY LLC-07-26-00004","supplier":"NATIONAL DAIRY LLC","currency":null,
 *    "status":"Active","is_expired":false,"item_count":36,
 *    "items":[{"item_code":"0004020001","item_name":"BUTTERMILK 180 ML","rate":1.5,
 *              "uom":null,"qty":1.0}, …]}
 *
 * `currency` is null and every `uom` is null. The currency falls back to the company's base — the
 * same assumption the rest of this wire makes for bare money (CLAUDE.md). The UOM is NOT guessed:
 * it stays null and the screen shows the rate against the line's own unit.
 */
export function toBlanketOrderDetail(w: WireBlanketOrderDetail): BlanketOrderDetail {
  const rows = Array.isArray(w.items) ? (w.items as Record<string, unknown>[]) : []
  return {
    name: str(w.name),
    supplier: str(w.supplier),
    currency: str(w.currency) || getBaseCurrency(),
    items: rows
      .map((r) => ({
        item_code: str(r.item_code),
        item_name: str(r.item_name),
        rate: num(r.rate),
        uom: r.uom == null ? null : str(r.uom),
        qty: num(r.qty),
      }))
      .filter((r) => r.item_code !== ''),
  }
}

/* ============ items.get_supplier_emails ============ */

export interface SupplierEmails {
  to: string[]
  cc: string[]
}


/**
 * Resolve each supplier's address — the ONE source that works. `Supplier.primary_email` is always
 * `''` live, and `RFQ.supplier_email` is null on most rows.
 *
 * ---- The shape, measured 2026-08-08 ----
 *
 * ```
 * GET items.get_supplier_emails?suppliers=["Shiby"]
 *   → TypeError: get_supplier_emails() missing 1 required positional argument: 'supplier'
 * GET items.get_supplier_emails?supplier=Shiby
 *   → {"supplier":"Shiby","supplier_name":"Shiby","email":"shibyavu@gmail.com"}
 * ```
 *
 * **Singular `supplier`, one per call, and a flat object with a bare `email` string** — not the
 * `suppliers=[…]` array and `{code: {to, cc}}` map this used to send and expect. That mismatch
 * meant the query threw, `emailBySupplier` was empty, and every send and every reminder failed
 * with "No resolvable email address" — the whole RFQ flow, unsendable.
 *
 * The plural form DID work earlier on 2026-08-07 (measured), so the server changed under us. The
 * mock had been built to the plural shape too, which is exactly why this looked verified: a mock
 * that matches the client instead of the server makes a wrong shape look right.
 *
 * Re-measured 2026-08-28 and still singular-only — `suppliers=[…]` answers `500 TypeError: …
 * missing 1 required positional argument: 'supplier'` on BOTH `items.` and `masters.` (one shared
 * implementation), and a supplier with no address answers `200 {"email": ""}` rather than an
 * error, which is why `trimmed(row?.email) !== ''` is the test for "has an address". Asked for as
 * `D9`; until it lands, one request per supplier is the only option and callers must bound how
 * many they ask for (see `lib/rfq.ts`'s `MAX_EMAIL_LOOKUPS`).
 *
 * One request per supplier because the method takes one. `Promise.all` is safe here — these are
 * independent reads, not writes, so there is no ordering to preserve.
 *
 * A supplier the server cannot resolve yields NO entry rather than an entry with an empty `to`:
 * `mailRfq` refuses to send to an empty list, and a present-but-empty entry would read as "we
 * looked and they have no address" when we may simply have failed to ask.
 */
export function useSupplierEmails(suppliers: string[]) {
  const key = [...new Set(suppliers.filter(Boolean))].sort()
  return useQuery({
    queryKey: ['items', 'supplier-emails', key] as const,
    queryFn: async (): Promise<Record<string, SupplierEmails>> => {
      // `lookupSupplierAddresses` (2026-09-22): the server's answer, then the supplier's Contact
      // documents — where 1,633 suppliers' addresses actually are. It never throws, so one
      // unresolvable supplier cannot blank the addresses of every other supplier on the enquiry.
      const rows = await Promise.all(key.map(async (supplier) => [supplier, await lookupSupplierAddresses(supplier)] as const))
      const out: Record<string, SupplierEmails> = {}
      for (const [supplier, addresses] of rows) if (addresses) out[supplier] = addresses
      return out
    },
    enabled: key.length > 0,
  })
}

/**
 * The same addresses, cached PER SUPPLIER rather than per set — for the picker's row labels.
 *
 * `useSupplierEmails` above keys one query on the whole sorted set, which is right for sending
 * (the set is fixed at send time and fetched once). It is wrong for a checklist the purchaser is
 * still ticking: every tick makes a new set, so a new key, so every address in it is fetched
 * again. Ticking ten suppliers one at a time costs 1+2+…+10 = 55 requests for ten addresses.
 *
 * Per-code queries make that ten. A supplier already looked up is a cache hit forever after,
 * including on the next open of the dialog and including from the send path if it lands on the
 * same code. Same method, same failure handling (an unresolvable supplier yields no entry rather
 * than a blank address — see `useSupplierEmails`); only the cache granularity differs.
 */
/**
 * How many address lookups may be in flight at once.
 *
 * A browser allows about six connections per host. Firing two hundred of these together does not
 * merely make them slow — it STARVES everything else on the page. The supplier search sat on
 * "Searching…" for as long as the queue took, because its own request could not get a connection
 * (measured 2026-08-31: the search is 1.1s alone, each lookup 1.12s, and the dialog fired up to
 * 250 of them the moment it opened).
 *
 * Three leaves half the pool for the work a purchaser is actually waiting on. Addresses fill in
 * progressively rather than together, which is the right trade: the list is usable immediately,
 * and a search narrows it to a handful that resolve in seconds.
 */
const EMAIL_LOOKUP_CONCURRENCY = 3

export function useSupplierEmailsByCode(suppliers: string[]) {
  // `sort: false` — the caller's order is deliberate (selection first) and the window below walks
  // it. `codeKey` rather than `join(',')`: a supplier name on this bench contains a comma
  // ("SEA LAND FOODSTUFF TRADG LLC,AJMAN") and splitting it back shredded it.
  const key = codeKey(suppliers, { sort: false })
  /*
    Deliberately NOT sorted. The caller orders these on purpose (selection first), and the window
    below walks them in order — so the suppliers already committed to resolve first.
  */
  const unique = useMemo(() => codesFromKey(key), [key])

  /*
    A sliding window, not a free-for-all: `settled` counts the lookups that have finished, so the
    window opens by one each time one lands. Never more than `EMAIL_LOOKUP_CONCURRENCY` are
    enabled at once, and the list walks itself to completion with no queue of its own.
  */
  const [settled, setSettled] = useState(0)
  // A different set of suppliers starts a new walk.
  useEffect(() => setSettled(0), [key])

  const queries = useQueries({
    queries: unique.map((supplier, index) => ({
      queryKey: ['items', 'supplier-email', supplier] as const,
      // The row label shows the first address; the send uses every one (`useSupplierEmails`).
      queryFn: async () => (await lookupSupplierAddresses(supplier))?.to[0] ?? '',
      enabled: index < settled + EMAIL_LOOKUP_CONCURRENCY,
      staleTime: 5 * 60_000,
      // One query per row of a checklist — a window refocus must not refetch the whole list.
      refetchOnWindowFocus: false,
    })),
  })

  // Advance the window as lookups land. A cached one settles at once, so re-opening the dialog
  // walks the whole list in a single pass without another request.
  const done = queries.filter((q) => q.isSuccess || q.isError).length
  useEffect(() => {
    setSettled((current) => (done > current ? done : current))
  }, [done])

  const byCode = useMemo(() => {
    const map = new Map<string, string>()
    for (let i = 0; i < unique.length; i++) {
      const email = queries[i]?.data
      if (email) map.set(unique[i], email)
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unique, queries.map((q) => q.dataUpdatedAt).join(',')])

  /** Per code, so a row whose own lookup has landed stops saying "looking up" while others run. */
  const isPending = (code: string) => {
    const index = unique.indexOf(code)
    return index >= 0 && queries[index] !== undefined && queries[index].isPending
  }

  return { byCode, isPending }
}

/* ============ items.get_suppliers_for_item ============ */

export interface WireItemSupplier {
  supplier: string
  supplier_name: string
  is_active: boolean
}

interface WireSuppliersForItem {
  item_code: string
  item_group: string
  suppliers: WireItemSupplier[]
}

export function useSuppliersForItem(itemCode: string) {
  return useQuery({
    queryKey: ['items', itemCode, 'suppliers'] as const,
    queryFn: async () => {
      const res = await callGet<WireSuppliersForItem>('items', 'get_suppliers_for_item', { item_code: itemCode })
      return res.suppliers
    },
    enabled: !!itemCode,
  })
}

/** `Item.candidate_suppliers` is `Supplier.code` only (the Link identity) — a row marked
 * `is_active: false` on `Item Group Supplier` is dropped rather than offered as a candidate. */
export function activeSupplierCodes(suppliers: WireItemSupplier[]): string[] {
  return suppliers.filter((s) => s.is_active).map((s) => s.supplier)
}

/* ============ items.get_blanket_orders ============ */

export interface WireBlanketOrder {
  name: string
  supplier: string
  supplier_name: string
  currency: string
  rate: number
  uom: string
  valid_from?: string | null
  valid_till?: string | null
  /** `Active` on the live wire; anything else must not price a line. */
  status?: string | null
}

interface WireBlanketOrders {
  item_code: string
  orders: WireBlanketOrder[]
}

export function toItemContracts(w: WireBlanketOrders): ItemContract[] {
  /*
    ---- B1 is FIXED: real orders now come back (measured 2026-08-14) ----

        GET items.get_blanket_orders?item_code=0005010001
          → { "orders": [ { "name": "AL MADINA BAKERY-07-26-00004",
                            "supplier": "AL MADINA BAKERY", "supplier_name": null,
                            "currency": null, "status": "Active", "rate": 2.25,
                            "uom": null, "qty": 1.0, "valid_from": null,
                            "valid_till": null, "blanket_order_type": "Purchasing" } ] }

    The header above used to say this endpoint answered `{"orders": []}` for everything. It does
    not any more, and the live payload turns two previously-harmless assumptions into real bugs.

    **`currency` is null.** `money(2.25, null)` produced a `Money` with no currency, which the
    display formats as a bare or broken label — on a rate that decides what the customer is
    charged. It falls back to the company's base currency, the same assumption the rest of this
    wire makes for bare numbers (CLAUDE.md: money is a bare number, currency is the company's).

    **`status` was ignored.** Only `Active` orders may price a line: an expired or cancelled
    blanket order silently pricing a provision line — and skipping the RFQ that would have found
    a real price — is the expensive kind of wrong. Anything not `Active` is dropped.

    `uom` is null too, so the rate's own unit is unknown; the line's UOM is what the screen shows
    against it. Worth asking the backend to populate, but not something to guess at here.
  */
  return w.orders
    .filter((o) => str(o.status) === '' || str(o.status).toLowerCase() === 'active')
    .map((o) => ({
      id: o.name,
      supplier: o.supplier,
      rate: money(o.rate, str(o.currency) || getBaseCurrency()),
      valid_till: o.valid_till ?? null,
    }))
}

export function useBlanketOrders(itemCode: string) {
  return useQuery({
    queryKey: ['items', itemCode, 'blanket-orders'] as const,
    queryFn: async () =>
      toItemContracts(await callGet<WireBlanketOrders>('items', 'get_blanket_orders', { item_code: itemCode })),
    enabled: !!itemCode,
    // Same opt-out as `useStock` above, and for the same reason: one query PER LINE ITEM, fired
    // only once `LineRow.tsx`'s `focused` latch trips so an enquiry with many lines doesn't fire
    // this for every row (its own comment). The global `refetchOnWindowFocus: true` (main.tsx)
    // would otherwise refetch every already-focused row's contracts on a single tab-switch —
    // the same N+1 the latch was built to avoid, just moved to a different trigger.
    refetchOnWindowFocus: false,
  })
}

/* `items.get_purchase_history` → `usePurchaseHistory` lives in `useSuppliers.ts`, not here —
 * that's the file its existing callers (`SupplierPickerModal`, `SupplierChoiceModal`) import it
 * from, and this task doesn't touch those files.
 */

/* ============================================================================================
 * Everything below is UNCHANGED from before this migration — `useItemsByCode` and `useItem`,
 * the batch/single item-by-code reads `/rfq`, `/compare`, `/quote`, `/revisions` and
 * `SupplierQuotationsPage` depend on. No live-verified `quoflow.items.*` equivalent exists yet
 * for a batch or single-document item read, so these stay on the old Frappe transport +
 * `mappers/item.ts` until a task gives them one.
 * ============================================================================================
 */

/**
 * The matched items for a set of lines, indexed by code. One `getResourceDoc` per code, run in
 * parallel — NOT a single filtered list (a list read has no `uom_conversions` at all, and this
 * Map feeds the UOM-mismatch/on-hand warnings this data exists for). A code Frappe has no
 * document for is dropped rather than failing the whole batch — the caller already treats a
 * missing map entry as "unknown", not as an error.
 */
/**
 * Indicative unit prices for a set of item codes, from the `Item Price` doctype.
 *
 * ---- Why this is a separate fetch ----
 *
 * `Item` on Quoflow is a lean 11-field doctype with no price at all — no `standard_rate`, no
 * `valuation_rate`. Pricing lives in `Item Price`, one row per (item, price list, UOM). That is
 * why `mappers/item.ts` set `base_price: null` and why the RFQ table showed no price (D1).
 *
 * ---- Which row wins: the IN-HOUSE cost, when there is one ----
 *
 * The purchaser wants the in-house (buying) price. `Price List` marks each list `buying` or
 * `selling`, so buying lists are preferred outright. The problem is coverage:
 *
 *     items with a BUYING  price:    10
 *     items with a SELLING price:  4253
 *
 * Preferring buying and stopping there would blank the column for 99.8% of the catalogue. So it
 * falls back to a selling price — and **says which it is showing**, because a selling price
 * presented as a cost would wreck a margin decision. `priceSource` carries the list name and
 * whether it is a buying list; the RFQ table puts that in the cell's tooltip.
 *
 * Within a tier, `preferredList` (the enquiry's own `Opportunity.price_list`) wins, then a
 * currency match.
 *
 * **Not every item has any price.** The rest — including the test items on this bench — have no
 * row at all, and those stay `null` rather than showing 0, which would read as "free".
 */
const ITEM_PRICE_DOCTYPE = 'Item Price'

/** Where a displayed price came from, so the UI can label a selling price as not a cost. */
export interface PriceSource {
  price_list: string
  /** True when the row sits on a `Price List` flagged `buying` — an in-house cost. */
  buying: boolean
}

/** Frappe's `in` filter takes the whole set in one request rather than one call per code. */
/**
 * Which `Price List` rows are BUYING lists — fetched once per session, not per price query.
 *
 * `Price List` is a static master (a handful of rows) but the query costs ~1.5s on this bench,
 * and it used to run inside every `fetchItemPrices` call, sequentially AFTER the `Item Price`
 * read. So a catalogue picker showing prices paid ~0.8s for the prices and then ~1.5s more for a
 * flag table that never changes — which is why the price column sat on "…" for seconds while the
 * stock beside it had already resolved.
 *
 * Cached as a promise, so concurrent callers share one request rather than racing.
 */
let buyingListsPromise: Promise<Set<string>> | null = null

function fetchBuyingLists(): Promise<Set<string>> {
  buyingListsPromise ??= getResource<Record<string, unknown>>('Price List', {
    fields: ['name', 'buying'],
    filters: [['buying', '=', 1]],
    limit: 100,
  })
    .then((lists) => new Set(lists.data.map((row) => strLegacy(row.name))))
    .catch(() => {
      // Without the flags we cannot tell cost from selling; everything is then reported as
      // non-buying, which understates rather than misleads. Not memoised on failure — the next
      // caller retries rather than inheriting an empty set for the whole session.
      buyingListsPromise = null
      return new Set<string>()
    })
  return buyingListsPromise
}

async function fetchItemPrices(
  codes: string[],
  preferredList?: string | null,
  preferredCurrency?: string | null,
): Promise<Map<string, { price: Money; source: PriceSource }>> {
  const byCode = new Map<string, { price: Money; source: PriceSource }>()
  if (codes.length === 0) return byCode

  /*
    The two reads run TOGETHER. They are independent — the prices do not decide which lists to
    ask about — and running them in sequence made every price query cost the sum of both
    (~0.8s + ~1.5s) when it need only cost the slower. The buying-list master is cached for the
    session on top of that, so the steady-state cost is just the `Item Price` read.
  */
  const [pricesResult, buyingLists] = await Promise.all([
    getResource<Record<string, unknown>>(ITEM_PRICE_DOCTYPE, {
      fields: ['item_code', 'price_list', 'price_list_rate', 'currency', 'uom'],
      filters: [['item_code', 'in', codes]],
      // An item can be on several lists; a few rows each is the realistic ceiling.
      limit: Math.max(codes.length * 6, 50),
    }).catch(() => null),
    fetchBuyingLists(),
  ])
  // A missing price is a blank cell, never a broken table — the item itself already loaded.
  if (!pricesResult) return byCode
  const rows = pricesResult.data

  // Buying dominates: a real cost always beats a better-matching selling price.
  const score = (row: Record<string, unknown>): number => {
    let value = buyingLists.has(strLegacy(row.price_list)) ? 10 : 0
    if (preferredList && strLegacy(row.price_list) === preferredList) value += 2
    if (preferredCurrency && strLegacy(row.currency) === preferredCurrency) value += 1
    return value
  }

  const best = new Map<string, { row: Record<string, unknown>; score: number }>()
  for (const row of rows) {
    const code = strLegacy(row.item_code)
    if (code === '') continue
    const current = best.get(code)
    const next = score(row)
    if (!current || next > current.score) best.set(code, { row, score: next })
  }
  for (const [code, { row }] of best) {
    const rate = numLegacy(row.price_list_rate)
    // 0 is not a price. A genuinely free line is not a thing here, and rendering "AED 0" would
    // read as one.
    if (rate > 0) {
      byCode.set(code, {
        price: moneyLegacy(rate, row.currency),
        source: { price_list: strLegacy(row.price_list), buying: buyingLists.has(strLegacy(row.price_list)) },
      })
    }
  }
  return byCode
}

/**
 * EVERY `Item Price` row for a set of codes — one per (list, unit) — for `inHousePriceFor`.
 *
 * `fetchItemPrices` below collapses to one row per item and discards the unit, which is right
 * for a picker column and wrong for pricing a line: the table holds a row PER UNIT (1707050028
 * is 1.00/PKT and 40.00/CTN on the same list), and a line asking in cartons needs the carton
 * row. The RFQ page reads these and resolves per line, per unit, per the delivery location's
 * list (`lib/inHousePrice.ts`). Same single `in`-filtered request, same buying-flag cache.
 */
async function fetchItemPriceRows(codes: string[]): Promise<Map<string, PriceRow[]>> {
  const byCode = new Map<string, PriceRow[]>()
  if (codes.length === 0) return byCode
  const [pricesResult, buyingLists] = await Promise.all([
    getResource<Record<string, unknown>>(ITEM_PRICE_DOCTYPE, {
      fields: ['item_code', 'price_list', 'price_list_rate', 'currency', 'uom'],
      filters: [['item_code', 'in', codes]],
      limit: Math.max(codes.length * 6, 50),
    }).catch(() => null),
    fetchBuyingLists(),
  ])
  if (!pricesResult) return byCode
  for (const row of pricesResult.data) {
    const code = strLegacy(row.item_code)
    if (code === '') continue
    const list = byCode.get(code) ?? []
    list.push({
      price_list: strLegacy(row.price_list),
      rate: numLegacy(row.price_list_rate),
      currency: strLegacy(row.currency) || getBaseCurrency(),
      uom: strLegacy(row.uom),
      buying: buyingLists.has(strLegacy(row.price_list)),
    })
    byCode.set(code, list)
  }
  return byCode
}

export function useItemPriceRows(codes: string[]) {
  const key = codeKey(codes)
  const unique = useMemo(() => codesFromKey(key), [key])
  return useQuery({
    queryKey: ['item-price-rows', key] as const,
    queryFn: () => fetchItemPriceRows(unique),
    enabled: unique.length > 0,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  })
}

/**
 * Prices for a set of item codes, batched — the catalogue picker's price column.
 *
 * ---- Why this does not reopen the N+1 the picker's own header warns about ----
 *
 * `CatalogPickerModal` deliberately fetches nothing per row: the catalogue runs to 18,836 items,
 * and three lookups per visible row is an N+1 across the whole thing. That argument is about
 * PER-ROW requests, and `fetchItemPrices` is not one — it is a single `Item Price` query with an
 * `in` filter over every visible code, so a hundred results cost one request, not a hundred.
 *
 * The prices are real: `Item Price` carries genuine rates (1.80 / 3.00 / 4.25 AED, measured
 * 2026-08-11). Coverage is the catch, and it is `fetchItemPrices` that handles it — ten items
 * have a BUYING price against 4,253 with a selling one, so a buying-only column would be blank
 * for 99.8% of the catalogue. It falls back to selling and reports which it used, and the picker
 * puts that in the cell's tooltip: a selling price mistaken for a cost wrecks a margin decision.
 */
export function useItemPrices(codes: string[]) {
  // `codeKey`, NOT `join(',')` — a real item code contains a comma and splitting it back on one
  // destroyed the code before the request was made. See `codeKey.ts`.
  const key = codeKey(codes)
  const unique = useMemo(() => codesFromKey(key), [key])

  return useQuery({
    queryKey: ['item-prices', key] as const,
    queryFn: () => fetchItemPrices(unique),
    enabled: unique.length > 0,
    // Prices do not move while somebody scrolls a picker.
    staleTime: 5 * 60_000,
  })
}

/**
 * Whether this deployment can answer "how much is in stock?" at all — ONE probe, not one per row.
 *
 * `items.get_stock` answers `{stock_enabled: false, message: "ERP not configured in Bosun
 * Settings"}` on the current bench, and there is no `Bin` doctype here either (404, measured
 * 2026-08-11), so no stock ledger exists locally: the figure lives in an ERP that Bosun Settings
 * has not been pointed at (`erp_type`/`erp_base_url`/`erp_api_key`/`erp_api_secret`, already
 * asked for).
 *
 * The probe exists so a list can show the stock column WITHOUT firing a request per row for an
 * answer that is presently "no" every time. It asks about one item — the flag it reads is a
 * deployment setting, not a property of that item — and caches for the session.
 */
export function useStockEnabled(probeItemCode: string | undefined) {
  return useQuery({
    queryKey: ['items', 'stock-enabled'] as const,
    queryFn: async () => toStock(await callGet<WireStock>('items', 'get_stock', { item_code: probeItemCode })).stock_enabled,
    enabled: !!probeItemCode,
    // A settings change requires a reload to matter anyway.
    staleTime: Infinity,
    // "Unknown" must not read as "no stock": callers render `—`, never a zero.
    retry: false,
  })
}

/* ============ items.get_items_by_codes — the batch read ============ */

interface WireItemByCode {
  item_code?: unknown
  item_name?: unknown
  item_group?: unknown
  stock_uom?: unknown
  impa_number?: unknown
  category?: unknown
  uom_conversions?: unknown
  base_price?: unknown
  price_list?: unknown
  price_currency?: unknown
}

/**
 * `{data: {code: item | null}}` → the Map the RFQ table reads, plus where each price came from.
 *
 * ---- Why this replaced N single reads ----
 *
 * `useItemsByCode` used to run one `getResourceDoc` PER CODE — 53 requests on a 53-line enquiry,
 * six at a time in a browser, at ~0.85s each. `items.get_items_by_codes` answers the whole set in
 * ONE POST in 0.84s (measured 2026-08-31) and carries the item's price with it, so the separate
 * `Item Price` query goes too. The method was unroutable on this bench when the per-code loop was
 * written (nginx 301 however it was called); it works now.
 *
 * A code the catalogue does not have comes back as an explicit `null` and is DROPPED from the
 * map, never stubbed. `LineRow` distinguishes "still loading" from "not in catalogue" on exactly
 * that absence, and a stub would turn a rescuable row into one claiming an item that isn't there.
 *
 * `buyingLists` decides only how the price is LABELLED. Ten items on this bench carry a real
 * buying price against 4,253 with only a selling one, so most of what this returns is a selling
 * price — presented as a cost it would quietly wreck a margin decision.
 */
export function toItemsByCode(
  payload: { data?: Record<string, WireItemByCode | null> } | null | undefined,
  buyingLists: ReadonlySet<string>,
): { byCode: Map<string, Item>; priceSource: Map<string, PriceSource> } {
  const byCode = new Map<string, Item>()
  const priceSource = new Map<string, PriceSource>()
  for (const [code, row] of Object.entries(payload?.data ?? {})) {
    // `null` is the catalogue saying it has no such item — see the header.
    if (!row) continue
    const list = str(row.price_list)
    const amount = row.base_price
    const conversions = Array.isArray(row.uom_conversions) ? (row.uom_conversions as Record<string, unknown>[]) : []
    byCode.set(code, {
      code,
      name: str(row.item_name),
      category: row.category && CATEGORY_SET.has(String(row.category)) ? (String(row.category) as ItemCategory) : undefined,
      group: str(row.item_group),
      base_uom: str(row.stock_uom),
      impa_number: str(row.impa_number) || undefined,
      uom_conversions: conversions.map((c) => ({ uom: str(c.uom), factor: num(c.conversion_factor, 1) })),
      // `null`, not 0 — "the catalogue has no price for this" is a fact about the item, and a
      // zero would read as free.
      base_price: typeof amount === 'number' ? money(amount, str(row.price_currency) || getBaseCurrency()) : null,
      on_hand: undefined, // → `items.get_stock`, per item and behind a latch
      candidate_suppliers: [], // → `items.get_suppliers_for_item`
      contracts: [], // → the selected blanket orders (`lib/blanketOrders.ts`)
    })
    if (list !== '') priceSource.set(code, { price_list: list, buying: buyingLists.has(list) })
  }
  return { byCode, priceSource }
}

/**
 * `priceList` is SENT on the request and, today, IGNORED by the server (measured 2026-09-14:
 * `USD`, `STANDARD` and none answer the same row — `D35`). It is sent anyway, and keyed into the
 * cache, so the day `get_items_by_codes` honours it the prices follow the delivery location's
 * list with no further change here. Until then `lib/inHousePrice.ts` applies the preference
 * over the `Item Price` rows itself.
 */
export function useItemsByCode(codes: string[], priceList?: string | null) {
  // `codeKey`, NOT `join(',')` — a real item code contains a comma and splitting it back on one
  // destroyed the code before the request was made. See `codeKey.ts`.
  const key = codeKey(codes)
  const unique = useMemo(() => codesFromKey(key), [key])

  /*
    ---- ONE request for the whole table (2026-08-31) ----

    This was a `useQueries` loop running one `getResourceDoc` per code, plus a separate batched
    `Item Price` query. On a 53-line enquiry that is 53 requests, six concurrent, ~0.85s each —
    most of the wait the "preparing" panel was covering for.

    `items.get_items_by_codes` answers the entire set in one POST (0.84s measured) and carries
    each item's price with it, so both go away.

    The per-code loop existed for a real reason, recorded here so it is not reinstated by reflex:
    a single query keyed on the whole code SET refetches everything whenever any one line's item
    changes. That still happens — but the refetch is now ONE request rather than N, so it costs
    ~0.84s instead of ~30 × 0.85s. The trade only ever favoured per-code while the batch endpoint
    was unroutable, which it no longer is.
  */
  const query = useQuery({
    queryKey: ['items', 'by-codes', key, priceList ?? ''] as const,
    queryFn: async () => {
      // Buying flags in parallel: a small static master, cached for the session by
      // `fetchBuyingLists`, needed only to label a price as cost vs selling.
      const [page, buyingLists] = await Promise.all([
        callPost<{ data?: Record<string, WireItemByCode | null> }>('items', 'get_items_by_codes', {
          item_codes: unique,
          ...(priceList ? { price_list: priceList } : {}),
        }),
        fetchBuyingLists(),
      ])
      // `asked`: the codes THIS answer covers. While a changed set is being read the previous
      // answer stays on screen (`placeholderData`), and only the codes it was asked for may be
      // judged missing from the master — see `codesMissingFromMaster` (2026-09-23).
      return { ...toItemsByCode(page, buyingLists), asked: new Set(unique) }
    },
    enabled: unique.length > 0,
    // A matched item does not change while a purchaser triages the enquiry it is on.
    staleTime: 5 * 60_000,
    // Keeps the table populated while a changed code set is re-read, rather than blanking it.
    placeholderData: (previous) => previous,
  })

  const byCode = query.data?.byCode ?? EMPTY_ITEMS
  const priceSource = query.data?.priceSource ?? EMPTY_PRICE_SOURCES
  const resolvedCodes: ReadonlySet<string> = query.data?.asked ?? EMPTY_CODES

  /*
    `isResolved` answers the question callers actually have — is `byCode` trustworthy yet? — and
    must be TRUE for an empty code set, which is a settled state rather than a loading one.
    `isLoading` (not `isPending`) because a disabled query stays `pending` forever in React Query
    v5, which would hold the RFQ page's preparing panel open indefinitely.
  */
  const isResolved = unique.length === 0 || !query.isLoading
  return { byCode, priceSource, resolvedCodes, isResolved, isPending: !isResolved, error: query.error ?? null }
}

export function useItem(code: string | null | undefined) {
  return useQuery({
    queryKey: qk.item(code ?? ''),
    queryFn: async () => toItemLegacy(await getResourceDoc<Record<string, unknown>>(ITEM_DOCTYPE, code as string)),
    enabled: !!code,
  })
}

/* ============ bosun-search /v1/match, via the dev proxy ============ */

/**
 * Match customer text against the catalogue.
 *
 * Calls the same-origin `/search/v1/match` path that `vite.config.ts` proxies to `bosun-search`
 * with the bearer token injected server-side. Not a `quoflow.*` method and not `callPost`: the
 * service is not Frappe, has no `{message: …}` envelope, and answers the shape documented in
 * `lib/searchMatch.ts`.
 *
 * ---- Runs once, never on its own schedule ----
 *
 * Measured: **one line 15.9s, three lines 72s**, roughly linear. That rules out a normal query —
 * the global `refetchOnWindowFocus` would re-run a 70s call every time the tab regains focus —
 * and it rules out blocking the page. So it fires once per distinct set of texts, caches for the
 * session, and the RFQ table renders immediately with the lines filling in behind it.
 *
 * Production has no proxy; there it is `items.match_items` on Frappe (ask `S1`), gated by
 * `liveAvailability`'s `itemMatching`.
 */
/**
 * ONE cache entry per line text (2026-09-17). It used to be one entry for the whole enquiry —
 * a key over every line's text, one request chain behind it, results surfaced only when the
 * chain had finished. Cold, on seventy uploaded lines at ~16s each, that was twenty minutes of
 * "unmatched — click to match" while the answers piled up unseen (the mail path never showed it,
 * because `CreateEnquiryBar` prefetches while the mail is still being read).
 *
 * Per text: a line answered once is never asked again whatever else lands on the enquiry, an
 * upload's lines start answering within seconds, and the prefetch and the page share entries by
 * construction. `withMatchSlot` keeps only a few requests in flight.
 */
// Keyed on the scope too: the same wording matched under another company or type is another answer.
const matchKey = (text: string) => ['search', 'match-one', text, itemScopeParams()] as const

/** The texts worth asking about — blanks dropped, duplicates collapsed, order kept. */
const matchTexts = (texts: readonly string[]): string[] => [...new Set(texts.map((t) => t.trim()).filter((t) => t !== ''))]

/* ---- A small gate: at most MATCH_CONCURRENCY requests in flight ---- */
const MATCH_CONCURRENCY = 3
let inFlight = 0
const waiting: Array<() => void> = []
export async function withMatchSlot<T>(run: () => Promise<T>): Promise<T> {
  if (inFlight >= MATCH_CONCURRENCY) await new Promise<void>((resolve) => waiting.push(resolve))
  inFlight += 1
  try {
    return await run()
  } finally {
    inFlight -= 1
    waiting.shift()?.()
  }
}

/*
  One line per request since 2026-09-17 (it was chunks of three). The service is roughly linear
  at ~16s a line, so a single line is the smallest unit that can answer quickly and fail alone;
  `withMatchSlot` keeps three in flight, which is the same throughput the chunking bought.
*/

/**
 * `Authorization: Bearer <clerk jwt>` for the `/search` routes, or nothing.
 *
 * `currentClerkToken` directly, NOT `proxyBearer`: that helper deliberately withholds the token
 * whenever `apiBase()` is non-empty, because in that configuration `/api` addresses Frappe and must
 * never receive a Clerk JWT. `/search` is different — it is a bare same-origin path, so it always
 * reaches OUR server (`server/index.ts` in production, `vite.config.ts`'s rule in dev) whatever
 * `apiBase()` says. Routing this through `proxyBearer` would send no token exactly in search-only
 * mode, where the server verifies it and would answer 401.
 */
async function searchAuthHeaders(): Promise<Record<string, string>> {
  const bearer = await currentClerkToken()
  return bearer ? { Authorization: `Bearer ${bearer}` } : {}
}

/** Per request — one line each now. */
const MATCH_TIMEOUT_MS = 90_000

async function matchChunk(texts: string[]): Promise<Map<string, ItemMatch>> {
  const res = await fetch('/search/v1/match', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await searchAuthHeaders()) },
    // The enquiry's company and type ride along (2026-09-21) — `itemScope.ts`.
    body: JSON.stringify({ items: texts, ...itemScopeParams() }),
    signal: AbortSignal.timeout(MATCH_TIMEOUT_MS),
  })
  if (!res.ok) throw new Error(`Item matcher answered ${res.status}`)
  /*
    A 200 is not proof of an answer. With no proxy in front of it — a deployed build, or a dev
    server started before the `/search` rule existed — this path falls through to the SPA and the
    static server returns `index.html` with a cheerful 200. Parsing that produced
    `SyntaxError: Unexpected token '<'` once per chunk in the deployed app's console.

    Checking the content type turns that into one clear sentence about the deployment.
  */
  const contentType = res.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) {
    throw new Error('Item matcher is not reachable from this build (no /search proxy) — matched nothing.')
  }
  const parsed = parseMatchResponse(await res.json())
  // An unreadable payload is not an empty one — an empty Map would read as "nothing matched"
  // and be cached as that answer for the session.
  if (!parsed) throw new Error('Item matcher returned an unrecognised response')
  return parsed
}

/**
 * Match every text, in small sequential chunks, keeping whatever succeeds.
 *
 * Sequential rather than parallel on purpose: the service is slow because it is working, and
 * firing eight concurrent requests at it makes each one slower without finishing sooner.
 *
 * A failed chunk does NOT fail the batch — its lines simply stay unmatched while the rest are
 * offered. Only a total failure (nothing at all came back) throws, because that is the case where
 * the caller should show "unavailable" rather than a silently short list.
 */
/** One line's match — its own request, through the gate. */
const matchOne = (text: string): Promise<ItemMatch | null> =>
  withMatchSlot(async () => (await matchChunk([text])).get(text) ?? null)

/**
 * A FRESH answer for one text under the current scope, written into the cache so the rows read it
 * — the re-match button (2026-09-21). `fetchQuery` with `staleTime: 0` asks the matcher again
 * whatever the cache holds.
 */
export async function rematchText(text: string): Promise<ItemMatch | null> {
  const trimmed = text.trim()
  if (trimmed === '') return null
  return queryClient.fetchQuery({ queryKey: matchKey(trimmed), queryFn: () => matchOne(trimmed), staleTime: 0 })
}

/** The map every consumer reads. `EMPTY_MATCHES`-shaped: text → match. */
export type ItemMatches = Map<string, ItemMatch>

/** Test-only handles. */
export const matchKeyForTest = matchKey
export const matchTextsForTest = matchTexts

/** Shared by the hook and the prefetch, so both cache under identical options. */
const MATCH_QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  // Retrying doubles a wait already measured in minutes.
  retry: false,
} as const

/**
 * Matches for many lines, each landing as it arrives.
 *
 * `texts` decides WHICH entries are read (every line, so a matched line's candidates stay on
 * screen); `toSend` decides which are FETCHED (only the lines still lacking an item). A text the
 * cache already holds is read straight from it — that is how the mail path's prefetch pays off.
 */
export function useMatchItems(texts: string[], enabled = true, toSend?: () => string[]) {
  const wanted = matchTexts(texts)
  const fetchable = new Set(matchTexts(toSend ? toSend() : texts))
  return useQueries({
    queries: wanted.map((text) => ({
      queryKey: matchKey(text),
      queryFn: () => matchOne(text),
      enabled: enabled && fetchable.has(text),
      ...MATCH_QUERY_OPTIONS,
    })),
    combine: (results) => {
      const data: ItemMatches = new Map()
      results.forEach((r, i) => {
        if (r.data) data.set(wanted[i], r.data)
      })
      const pending = results.filter((r) => r.isFetching).length
      return {
        data,
        /** Ask again for every line that errored — the entries that answered are untouched. */
        refetch: () => Promise.all(results.filter((r) => r.isError).map((r) => r.refetch())),
        isPending: pending > 0,
        /** Answered lines, and how many are still being asked — for the progress line. */
        answered: results.filter((r) => r.data !== undefined).length,
        pending,
        isFetching: pending > 0,
        // "Loading" only while NOTHING has answered — the first result reveals the table.
        isLoading: pending > 0 && data.size === 0,
        isSuccess: results.length > 0 && results.every((r) => r.isSuccess),
        isError: results.length > 0 && results.every((r) => r.isError),
        errorCount: results.filter((r) => r.isError).length,
      }
    },
  })
}

export function prefetchItemMatches(texts: string[]): void {
  for (const text of matchTexts(texts)) {
    void queryClient.prefetchQuery({ queryKey: matchKey(text), queryFn: () => matchOne(text), ...MATCH_QUERY_OPTIONS }).catch(() => {})
  }
}

/* ============ imperative reads, for click handlers ============ */

/**
 * The item master record for one code, fetched on demand and shared with `useItemsByCode`'s cache.
 *
 * A click handler cannot call a hook, and applying an item needs the master's UOM list before it
 * can decide what unit the line should carry. Goes through `fetchQuery` so a code already on the
 * table is free, and so the row that renders next reads the same entry.
 */
export async function fetchItemByCode(code: string): Promise<Item | null> {
  if (!code) return null
  try {
    return await queryClient.fetchQuery({
      queryKey: qk.item(code),
      queryFn: async () => toItemLegacy(await getResourceDoc<Record<string, unknown>>(ITEM_DOCTYPE, code)),
      staleTime: 5 * 60_000,
    })
  } catch {
    // A code the catalogue cannot resolve is not fatal here — the caller writes the item anyway
    // and simply cannot pre-correct the UOM.
    return null
  }
}

/**
 * An item's ACTIVE blanket orders, fetched on demand and shared with `useBlanketOrders`' cache.
 *
 * Exists so applying an item can decide the line's ROUTE in the same write — a click handler
 * cannot call a hook. Empty on any failure: a line that cannot be proven to be on contract must
 * stay RFQ-routed, because that path still finds a price, and a wrongly contract-routed line
 * silently skips the RFQ that would have found one.
 */
export async function fetchBlanketOrders(code: string): Promise<ItemContract[]> {
  if (!code) return []
  try {
    return await queryClient.fetchQuery({
      queryKey: ['items', code, 'blanket-orders'] as const,
      queryFn: async () =>
        toItemContracts(await callGet<WireBlanketOrders>('items', 'get_blanket_orders', { item_code: code })),
      staleTime: 5 * 60_000,
    })
  } catch {
    return []
  }
}

/**
 * Every ACTIVE supplier the master associates with an item — the same list
 * `SupplierPickerModal` shows, from the same cache entry.
 *
 * Exists so matching an item can assign them in the same write. On a sixty-line enquiry, opening
 * a dialog per line to tick suppliers by hand is the single largest cost on the screen; assigning
 * them and letting the purchaser remove the odd one inverts that.
 *
 * Failure resolves to `[]`, never a throw: an item that could be matched must still be matched
 * even if its supplier list is unavailable.
 */
export async function fetchSuppliersForItem(code: string): Promise<string[]> {
  if (!code) return []
  try {
    const suppliers = await queryClient.fetchQuery({
      queryKey: ['items', code, 'suppliers'] as const,
      queryFn: async () =>
        (await callGet<WireSuppliersForItem>('items', 'get_suppliers_for_item', { item_code: code })).suppliers,
      staleTime: 5 * 60_000,
    })
    return activeSupplierCodes(suppliers)
  } catch {
    return []
  }
}

/**
 * Is the item matcher reachable from THIS deployment?
 *
 * ---- Why a probe and not a build-time flag ----
 *
 * The matcher lives behind a `/search` proxy that exists in two places: `vite.config.ts` in dev,
 * and `server/index.ts` in production — and the production one only mounts when
 * `BOSUN_SEARCH_URL` is set as an App Service setting. So "is this feature available?" is a
 * property of the DEPLOYMENT, not of the build, and no compile-time constant can answer it.
 *
 * Both wrong answers were shipped on 2026-08-13. Hard-coded `true`: the deployed app called
 * `/search/v1/match`, the setting was unset, the route did not exist, the SPA fallback returned
 * `index.html`, and every chunk failed with `SyntaxError: Unexpected token '<'`. Then
 * `import.meta.env.DEV`: correct for that broken deployment, but it would keep the feature dark
 * in production even once the setting was added.
 *
 * One `GET /search/healthz` answers it honestly in every case — proxy present and service up
 * (JSON), or anything else (HTML from the SPA fallback, a 502, a missing route). Cached for the
 * session; the answer cannot change without a redeploy.
 */
export function useSearchAvailable() {
  return useQuery({
    queryKey: ['search', 'available'] as const,
    queryFn: async () => {
      const res = await fetch('/search/healthz', { headers: await searchAuthHeaders() })
      // A 200 proves nothing on its own — the SPA fallback returns `index.html` with one.
      if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return false
      return true
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}
